import { clsx } from 'clsx'
import { MouseEvent, ReactNode, createContext, useContext, useState } from 'react'
import { DotsThreeIcon } from './icons/16x16'
import { PopoverMenu } from './PopoverMenu'

type ContextMenuProps = { children: ReactNode; className?: string }

const MenuContext = createContext({ closeMenu: () => {} })

export function ContextMenu({ children, className }: ContextMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <PopoverMenu
      isControlled={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      offset={{ dx: -8, dy: 0 }}
      trigger={
        <button
          aria-label="Menu"
          className={clsx(
            isOpen ? 'visible' : 'invisible',
            'size-6 outline-none group-hover:visible',
            className
          )}
        >
          <DotsThreeIcon aria-hidden="true" role="img" />
        </button>
      }
    >
      <div className="flex flex-col gap-y-0.5 rounded border border-solid border-stone-300 bg-white py-2 shadow outline-none">
        <MenuContext.Provider value={{ closeMenu: () => setIsOpen(false) }}>
          {children}
        </MenuContext.Provider>
      </div>
    </PopoverMenu>
  )
}

export function PrimaryContextMenu({ children }: ContextMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <PopoverMenu
      isControlled={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      offset={{ dx: -8, dy: 0 }}
      trigger={
        <button
          aria-label="Menu"
          className={clsx('size-6 outline-none')}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
          }}
        >
          <DotsThreeIcon role="img" />
        </button>
      }
    >
      <div className="flex flex-col gap-y-0.5 rounded border border-solid border-stone-300 bg-white py-2 shadow outline-none">
        <MenuContext.Provider value={{ closeMenu: () => setIsOpen(false) }}>
          {children}
        </MenuContext.Provider>
      </div>
    </PopoverMenu>
  )
}

type ContextMenuItemProps = {
  icon?: ReactNode
  children: ReactNode
  onClick?: () => void
  className?: string
}

export function ContextMenuItem({ icon, children, className, onClick }: ContextMenuItemProps) {
  const { closeMenu } = useContext(MenuContext)

  return (
    <div
      role="menuitem"
      onClick={(e) => {
        console.log('menu item clicked')
        e.stopPropagation()
        onClick?.()
        closeMenu()
      }}
      className={clsx(
        'flex min-w-12 cursor-pointer items-center gap-2 border-l border-transparent pb-0.5 pl-1.5 pr-2 pt-0.5 text-sm text-gray-900 outline-none',
        'hover:border-l-stone-400 hover:bg-stone-200',
        className
      )}
    >
      {icon && <div className="flex w-4 flex-row items-center justify-center">{icon}</div>}
      {children}
    </div>
  )
}
